import {
  ICareProposal,
  IDeliveryAddress,
  IDeliveryInterval,
  IHistoryArticleLine,
  IOrder,
  IProposedArticleLine,
  OrderStatus,
} from '@alberta/konexi-shared';
import { groupBy } from 'lodash';
import moment from 'moment';
import { addBusinessDays } from 'src/app/common/date/business-days';
import { getDeliveryEndDate } from 'src/app/common/date/order-dates';
import { InsuranceContractDB } from 'src/app/common/repository/databases';
import { createObject } from 'src/app/common/tracking/object-creator';
import { ignore, removeTime, tracked } from 'src/app/common/tracking/tracker';
import { BaseViewModel } from 'src/app/common/viewmodel/base-view-model';
import { QueryService } from 'src/app/shared/services/query/query.service';

import { IOrderMaximumQuantity } from '../contracts/order-maximum-quantity';
import {
  calcMaxQuanityBaseOnScope,
  calculateRecommendedQuantity,
  findProprosedArticleLineInCarepropsoal,
  getContractAndStandardCareProposal,
  loadArticlesOfArticleLines,
  transformProposedArticleToOrderedArticle,
} from '../order-functions';
import {
  getMaxQuantityForOrderedArticleLine,
  getOrderMaxQuantityForProposedArticleLine,
  updateMaxQuantityInOrderedArticleLines,
} from '../order-max-quantity-functions';
import { TenantConfigService } from './../../../shared/services/tenant-config/tenant-config.service';
import { OrderedArticleLineViewModel } from './ordered-article-line-view-model';

export class OrderViewModel extends BaseViewModel implements IOrder {
  deliveryDateChanged: boolean;
  dayOfDelivery: number;
  @removeTime()
  nextDelivery: Date;
  rootId: string;
  regionId: string;
  createdAt: Date;
  updatedAt: Date;
  updatedBy: string;
  salesId: string;
  patientId: string;
  therapyId: number;
  therapyTypeId: number;
  location: number;
  updated: Date;
  @removeTime()
  deliveryDate: Date;
  @removeTime()
  startDate: Date = new Date();
  @removeTime()
  endDate: Date = new Date();
  deliveryInterval: IDeliveryInterval;
  deliveryAddress: IDeliveryAddress;
  deliveryInformation: any;
  startTime: string;
  deliveryNote: string;
  deliveryDateUnknown: boolean;

  doctorId: string;
  hospitalId: string;
  @ignore(true)
  isHospitalPrescriber: boolean;

  pharmacyId: string;

  status: OrderStatus;

  isInitialCare: boolean;
  @tracked({ factory: createObject(OrderedArticleLineViewModel) })
  orderedArticleLines: OrderedArticleLineViewModel[] = [];
  articleLineHistory?: IHistoryArticleLine[] = [];

  isSeriesOrder = false;
  seriesOrderChanged = false;
  deliveryAddressChanged = false;

  annotation: string;

  createdBy: string;
  creationDate: Date;

  accountingMonth: number[];
  careProposalId: string;
  alreadyTransmitted: boolean;
  transmittedAt: string;

  /**
   * Indiviudal OrderViewModel Data
   */
  @ignore()
  public scope: number;
  @ignore()
  public associatedCareproposal: ICareProposal;

  /**
   * getter and setter
   */
  public get startDateVM(): Date {
    return this.startDate;
  }
  public set startDateVM(date: Date) {
    this.startDate = date;
    this.updateScope();
  }
  public get endDateVM(): Date {
    return this.endDate;
  }
  public set endDateVM(date: Date) {
    this.endDate = date;
    this.updateScope();
  }

  constructor() {
    super();

    this.isHospitalPrescriber = false;
  }

  /**
   *
   *
   * Initialize the the Order with the articlelines form the careproposal
   */
  public async initializeWithCareProposalData(
    careProposal: ICareProposal,
    queryService: QueryService,
    tenantConfigService: TenantConfigService,
    rootId?: string
  ) {
    await this.initializeVariablesFromCareProposal(careProposal, rootId, tenantConfigService);
    const groupedProposedArticleLines = groupBy(careProposal.proposedArticleLines, (item: IProposedArticleLine) => {
      return `${item.contractId} ${item.therapyProductGroupId}`;
    });
    for (const key of Object.keys(groupedProposedArticleLines)) {
      const groupedProposedArticleLinesEntries = groupedProposedArticleLines[key];
      const firstProposedArticleLine = groupedProposedArticleLinesEntries[0];
      const [contract, standardCareProposal] = await getContractAndStandardCareProposal(
        (careProposal || { standardCareProposalId: null }).standardCareProposalId,
        (firstProposedArticleLine || { contractId: null }).contractId,
        queryService
      );

      let maxQuantity: IOrderMaximumQuantity = null;
      maxQuantity = getOrderMaxQuantityForProposedArticleLine(
        contract,
        firstProposedArticleLine,
        standardCareProposal,
        this.scope
      );
      const articles = await loadArticlesOfArticleLines(groupedProposedArticleLinesEntries, queryService);
      for (const proposedArticleLine of groupedProposedArticleLinesEntries) {
        const articleLine = transformProposedArticleToOrderedArticle(
          proposedArticleLine,
          articles.find(article => article._id === proposedArticleLine.articleId),
          this.scope,
          contract,
          maxQuantity,
          !!rootId
        );
        this.orderedArticleLines.push(articleLine);
      }
    }
  }

  private async initializeVariablesFromCareProposal(
    careProposal: ICareProposal,
    rootId: string,
    tenantConfigService: TenantConfigService
  ) {
    this.associatedCareproposal = careProposal;
    this.careProposalId = careProposal._id;
    this.therapyId = careProposal.therapyId;
    this.therapyTypeId = careProposal.therapyTypeId;
    if (careProposal.location) {
      this.location = careProposal.location;
    }
    this.status = OrderStatus.Created;
    this.creationDate = new Date();
    this.regionId = careProposal.regionId;
    this.rootId = rootId;

    if (await tenantConfigService.mustSetDeliveryDate()) {
      this.deliveryDate = this.startDate = addBusinessDays(new Date(), 2);
      if (this.startDate && this.therapyId) {
        this.endDate = getDeliveryEndDate(this.startDate, this.therapyId);
        this.scope = this.calcScope();
      } else {
        throw new Error('Cant calculate Scope because startDate or therapyId is missing');
      }
    } else {
      this.scope = 28;
    }

    this.patientId = careProposal.patientId;
    this.doctorId = careProposal.doctorId;
    if (careProposal.pharmacyId) {
      this.pharmacyId = careProposal.pharmacyId;
    }
  }

  public clean() {
    // remove unnessary Articlelines
    for (let index = this.orderedArticleLines.length - 1; index >= 0; index--) {
      if (
        !this.isSeriesOrder &&
        this.orderedArticleLines[index].dosage &&
        this.orderedArticleLines[index].dosage.duration
      ) {
        delete this.orderedArticleLines[index].dosage.duration;
      }
      if (!this.orderedArticleLines[index].active) {
        this.orderedArticleLines.splice(index, 1);
      }
    }
    if (this.rootId) {
      this.startDate = undefined;
      this.endDate = undefined;
      this.accountingMonth = [];
    }
  }

  public async loadOrderVM(
    careproposal: ICareProposal,
    queryService: QueryService,
    loadMissingArticlesFromCareproposal?: boolean
  ) {
    if (!careproposal) {
      throw new Error('No  related Careproposal found!');
    }
    this.associatedCareproposal = careproposal;
    if (!this.rootId) {
      this.scope = this.calcScope();
    }
    await this.populateExistingArticleLines(queryService);
    if (loadMissingArticlesFromCareproposal) {
      await this.addNewArticleLinesFromCareProposalToOrder(careproposal, queryService);
    }
    this.updateArticleNotFoundFlagInOrderedArticleLines(careproposal);
    const groupByContractIdAndTherapyProductGroupId = (item: OrderedArticleLineViewModel) => {
      if (item) {
        return `${item.contractID} ${item.associatedArticle ? item.associatedArticle.productGroupId : undefined}`;
      }
    };
    const groupedArticlesLines = groupBy(this.orderedArticleLines, groupByContractIdAndTherapyProductGroupId);
    for (const key of Object.keys(groupedArticlesLines)) {
      const orderedArticleLines = groupedArticlesLines[key];
      const firstOrderedArticleLine = orderedArticleLines[0];
      const [contract, standardCareProposal] = await getContractAndStandardCareProposal(
        (careproposal || { standardCareProposalId: null }).standardCareProposalId,
        (firstOrderedArticleLine || { contractID: null }).contractID,
        queryService
      );

      const maxQuantity = getMaxQuantityForOrderedArticleLine(
        contract,
        firstOrderedArticleLine,
        standardCareProposal,
        careproposal,
        this.scope
      );

      updateMaxQuantityInOrderedArticleLines.call(this, orderedArticleLines, contract, maxQuantity, {
        setContract: (index, insuranceContract) =>
          (this.orderedArticleLines[index].associatedContract = insuranceContract),
        setCurrentQuantity: index => {
          this.orderedArticleLines[index].maxAmount.currentQuantity +=
            +this.orderedArticleLines[index].dosage.quantity *
            this.orderedArticleLines[index].associatedQuantityPerUnit.quantity;
        },
      });
    }
    this.scope = null;
    this.updateScope();
  }

  /*
   * check if the article is still in the careproposal
   */
  private updateArticleNotFoundFlagInOrderedArticleLines(careproposal: ICareProposal) {
    if (!careproposal || !careproposal.proposedArticleLines) {
      return;
    }
    this.orderedArticleLines.forEach(orderedArticleLine => {
      if (orderedArticleLine) {
        const orderedArticleLineFoundInProposedArticleLines = careproposal.proposedArticleLines.some(
          proposedArticleLine => proposedArticleLine.articleId === orderedArticleLine.articleId
        );
        orderedArticleLine.articleNotFoundInCareProposal = orderedArticleLineFoundInProposedArticleLines === false;
      }
    });
  }

  private async addNewArticleLinesFromCareProposalToOrder(
    careproposal: ICareProposal,
    queryService: QueryService
  ): Promise<void> {
    const articles = await loadArticlesOfArticleLines(careproposal.proposedArticleLines, queryService);

    for (const proposedArticleLine of careproposal.proposedArticleLines) {
      if (
        proposedArticleLine &&
        !this.orderedArticleLines.find(orderedLine => orderedLine.articleId === proposedArticleLine.articleId)
      ) {
        const newProposedArticleLine = transformProposedArticleToOrderedArticle(
          proposedArticleLine,
          articles.find(article => article._id === proposedArticleLine.articleId),
          this.calcScope(),
          await queryService.get(proposedArticleLine.contractId, InsuranceContractDB)
        );
        if (!newProposedArticleLine) {
          continue;
        }
        newProposedArticleLine.isNew = true;
        newProposedArticleLine.active = false;
        newProposedArticleLine.articleNotFoundInCareProposal = false;
        this.orderedArticleLines.push({ ...newProposedArticleLine });
      }
    }
  }

  private async populateExistingArticleLines(queryService: QueryService): Promise<void> {
    const articles = await loadArticlesOfArticleLines(this.orderedArticleLines, queryService);

    for (const orderedArticleLine of this.orderedArticleLines) {
      if (orderedArticleLine) {
        const article = articles.find(article1 => article1._id === orderedArticleLine.articleId);

        orderedArticleLine.isNew = false;
        orderedArticleLine.active = true;
        if (!article) {
          continue;
        }

        orderedArticleLine.associatedQuantityPerUnit = article.findPackagingUnit(orderedArticleLine.dosage.packagingId);
        orderedArticleLine.associatedArticle = article;
      }
    }
  }

  public calcScope(): number {
    if (this.rootId) {
      return 1;
    }
    if (!(this.startDate && this.endDate)) {
      throw new Error('Can not calculate scope');
    }
    return Math.floor(Math.abs(this.endDate.getTime() - this.startDate.getTime()) / (1000 * 60 * 60 * 24));
  }

  private updateScope() {
    const result = this.calcScope();
    if (result && result !== this.scope) {
      this.scope = result;
      this.updateScopeInOrderedArticleLines();
    }
  }

  private updateScopeInOrderedArticleLines() {
    if (!this.associatedCareproposal) {
      throw new Error('No associated careproposal found. Please initizale the viewmodel with one');
    }
    this.orderedArticleLines.forEach((orderArticleLine, index) => {
      if (orderArticleLine) {
        const proposedArticleLine = findProprosedArticleLineInCarepropsoal(
          this.associatedCareproposal,
          orderArticleLine.articleId
        );
        if (proposedArticleLine) {
          this.orderedArticleLines[index].recommendedQuantity = calculateRecommendedQuantity(
            orderArticleLine.associatedQuantityPerUnit,
            proposedArticleLine,
            this.scope
          );
        } else {
          this.orderedArticleLines[index].articleNotFoundInCareProposal = true;
          this.orderedArticleLines[index].recommendedQuantity = this.orderedArticleLines[index].dosage.quantity;
        }
        if (orderArticleLine.maxAmount) {
          orderArticleLine.maxAmount.maxQuantityBaseOnScope = calcMaxQuanityBaseOnScope(
            orderArticleLine.maxAmount.maxQuantity,
            this.scope
          );
        }
      }
    });
  }

  public getDeliveryStartTime() {
    if (this.startTime) {
      return moment(`01.01.1970 ${this.startTime}`, 'DD.MM.YYYY HH:mm').utc().toDate();
    }
  }
  public setDeliveryStartTime(startTime: Date) {
    if (startTime) {
      this.startTime = moment(startTime).format('HH:mm');
    }
  }
}
